import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { DRONES, track } from "@/data/content";
import { Reveal, SectionTag, TituloLineas, useInViewSafe, useReduceMotion } from "@/components/Reveal";

const CIFRAS = { t100: "100 L", t70p: "70 L", t55: "50 L", mavic3m: "200 ha por vuelo" };

// Mismo glifo de dron que recorre las rutas del mapa de Bolivia.
function DronMarca({ className = "" }) {
  return (
    <svg viewBox="-11 -8 22 16" className={className} fill="none" stroke="currentColor" aria-hidden="true">
      <line x1="-7" y1="-5" x2="7" y2="5" strokeWidth="1.4" />
      <line x1="7" y1="-5" x2="-7" y2="5" strokeWidth="1.4" />
      <circle cx="-7" cy="-5" r="2.6" strokeWidth="1.2" />
      <circle cx="7" cy="-5" r="2.6" strokeWidth="1.2" />
      <circle cx="-7" cy="5" r="2.6" strokeWidth="1.2" />
      <circle cx="7" cy="5" r="2.6" strokeWidth="1.2" />
      <circle cx="0" cy="0" r="2.2" fill="currentColor" stroke="none" />
    </svg>
  );
}

// Cifra de la tarjeta: cuenta de 0 a su valor cuando el dron viajero la
// enciende (1 s). Con «reducir movimiento» queda fija en su valor.
function CifraQueCuenta({ texto, encendida, testid }) {
  const reduce = useReduceMotion();
  const [p, setP] = useState(0);
  const num = parseInt(texto.match(/\d[\d.,]*/)?.[0]?.replace(/[.,]/g, "") ?? "0", 10);
  const despues = texto.match(/\d[\d.,]*/)?.index ?? 0;
  const largo = (texto.match(/\d[\d.,]*/)?.[0] ?? "").length;

  useEffect(() => {
    if (!encendida) return;
    if (reduce) {
      setP(1);
      return;
    }
    const t0 = performance.now();
    let raf;
    const paso = (t) => {
      const q = Math.min(1, (t - t0) / 1000);
      setP(1 - Math.pow(1 - q, 3));
      if (q < 1) raf = requestAnimationFrame(paso);
    };
    raf = requestAnimationFrame(paso);
    return () => cancelAnimationFrame(raf);
  }, [encendida, reduce]);

  return (
    <p data-testid={testid} className="font-mono text-3xl font-extrabold tracking-tight text-certero sm:text-4xl">
      {texto.slice(0, despues)}
      {Math.round(num * p).toLocaleString("es-BO")}
      {texto.slice(despues + largo)}
    </p>
  );
}

function FotoTarjeta({ drone }) {
  if (drone.portada || drone.img) {
    return (
      <span className="relative block overflow-hidden">
        <img
          src={drone.portada || drone.img}
          alt={drone.alt}
          loading="lazy"
          decoding="async"
          className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
        />
        <span className="absolute inset-0 bg-gradient-to-t from-abyss/70 via-transparent to-transparent" aria-hidden="true" />
      </span>
    );
  }
  // Sin foto de portada aún (T55): recorte oficial sobre panel azul.
  return (
    <span className="relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden bg-[#0c295c]/60">
      <span
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{ background: "radial-gradient(circle at 50% 120%, #52c234 0%, transparent 60%)" }}
        aria-hidden="true"
      />
      <img
        src={drone.producto}
        alt={`Vista frontal oficial del ${drone.modelo}`}
        loading="lazy"
        decoding="async"
        className="w-[72%] max-w-[300px] drop-shadow-[0_14px_24px_rgba(0,0,0,0.45)] transition-transform duration-500 group-hover:scale-[1.05]"
      />
    </span>
  );
}

function TarjetaDron({ drone, encendida, testidBase }) {
  return (
    <div
      data-testid={`${testidBase}-${drone.slug}`}
      className={`group relative h-full border transition-[border-color,transform,background-color] duration-500 ${
        encendida ? "border-certero bg-surface" : "border-hud bg-ink"
      } hover:-translate-y-2 hover:border-certero`}
    >
      <span
        data-testid={`${testidBase}-punto-${drone.slug}`}
        aria-hidden="true"
        className={`absolute -top-[5px] left-1/2 z-10 h-2.5 w-2.5 -translate-x-1/2 rounded-full border border-certero transition-[background-color,box-shadow] duration-500 ${
          encendida ? "bg-certero shadow-[0_0_14px_rgba(82,194,52,0.9)]" : "bg-abyss group-hover:bg-certero"
        }`}
      />
      <FotoTarjeta drone={drone} />
      <div className="flex flex-col p-6">
        <h3 className="font-display text-lg font-bold uppercase leading-snug tracking-wide text-mist">{drone.modelo}</h3>
        <div className="mt-4">
          <CifraQueCuenta
            texto={CIFRAS[drone.id]}
            encendida={encendida}
            testid={`${testidBase}-cifra-${drone.slug}`}
          />
        </div>
        <Link
          data-testid={`ofrecemos-btn-ver-${drone.slug}`}
          to={`/drones/${drone.slug}`}
          onClick={() => track("ver_dron_click", { modelo: drone.modelo, origen: "lo_que_ofrecemos" })}
          className="group/btn mt-5 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.15em] text-certero transition-colors duration-300 hover:text-[#4bc443]"
        >
          Ver el {drone.modelo.replace("DJI ", "")}
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
        </Link>
      </div>
    </div>
  );
}

// Computadora: 4 tarjetas en fila, unidas por una línea punteada. Al entrar el
// bloque, un dron pequeño recorre la línea en 3 s y enciende cada tarjeta al
// pasar (borde verde, foto con acercamiento y cifra contando). Al final queda
// quieto sobre la última.
function FilaOfrecemos() {
  const [ref, inView] = useInViewSafe(0.3);
  const reduce = useReduceMotion();
  const [lit, setLit] = useState([false, false, false, false]);

  useEffect(() => {
    if (reduce) {
      setLit([true, true, true, true]);
      return;
    }
    if (!inView) return;
    const timers = [400, 1100, 1900, 2600].map((ms, i) =>
      setTimeout(() => setLit((s) => { const n = [...s]; n[i] = true; return n; }), ms)
    );
    return () => timers.forEach(clearTimeout);
  }, [inView, reduce]);

  const viaja = inView && !reduce;
  return (
    <div ref={ref} data-testid="ofrecemos-fila" className="relative mt-16 hidden lg:grid lg:grid-cols-4 lg:gap-5">
      <div className="pointer-events-none absolute inset-x-0 -top-px z-10 hidden lg:block" aria-hidden="true">
        <div className="w-full border-t border-dashed border-hud" />
        {viaja && (
          <motion.span
            data-testid="ofrecemos-dron-viajero"
            className="absolute -top-3.5 text-certero"
            initial={{ left: "0%" }}
            animate={{ left: "87.5%" }}
            transition={{ duration: 3, ease: "easeInOut" }}
          >
            <DronMarca className="h-7 w-7" />
          </motion.span>
        )}
      </div>
      {DRONES.map((d, i) => (
        <TarjetaDron key={d.id} drone={d} encendida={lit[i]} testidBase="ofrecemos-tarjeta" />
      ))}
    </div>
  );
}

// Celular: carrusel para deslizar con el dedo, una por pantalla, con puntos.
// Cada tarjeta se enciende al quedar en el centro.
function CarruselOfrecemos() {
  const [ref, inView] = useInViewSafe(0.3);
  const scroller = useRef(null);
  const [idx, setIdx] = useState(0);

  const onScroll = () => {
    const el = scroller.current;
    if (!el || !el.clientWidth) return;
    setIdx(Math.max(0, Math.min(DRONES.length - 1, Math.round(el.scrollLeft / el.clientWidth))));
  };
  const ir = (i) => {
    const el = scroller.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  };

  return (
    <div ref={ref} className="mt-12 lg:hidden">
      <div
        ref={scroller}
        onScroll={onScroll}
        data-testid="ofrecemos-carrusel"
        className="flex snap-x snap-mandatory gap-4 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {DRONES.map((d, i) => (
          <div key={d.id} className="w-full shrink-0 snap-center">
            <TarjetaDron drone={d} encendida={inView && i === idx} testidBase="ofrecemos-carrusel-tarjeta" />
          </div>
        ))}
      </div>
      <div className="mt-5 flex items-center justify-center gap-2">
        {DRONES.map((_, i) => (
          <button
            key={i}
            data-testid={`ofrecemos-punto-${i}`}
            onClick={() => ir(i)}
            aria-label={`Ver el ${DRONES[i].modelo}`}
            className={`h-2 rounded-full transition-all duration-300 ${i === idx ? "w-6 bg-certero" : "w-2 bg-mist/30 hover:bg-mist/60"}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function Drones() {
  return (
    <section id="drones" className="scroll-mt-20 bg-abyss px-4 py-14 sm:px-8 sm:py-20 lg:px-12 lg:py-32">
      <div className="mx-auto max-w-[1400px]">
        <Reveal>
          <SectionTag>Lo que ofrecemos</SectionTag>
          <TituloLineas
            lineas={["Los drones DJI que trabajan", "su campo"]}
            className="mt-5 max-w-2xl font-display text-2xl font-bold uppercase leading-tight tracking-tight text-mist sm:text-3xl lg:text-4xl"
          />
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-smoke sm:text-base">
            Tres Agras para aplicar en el cultivo y el Mavic 3M para medirlo.
          </p>
        </Reveal>

        <FilaOfrecemos />
        <CarruselOfrecemos />

        <Reveal delay={0.1} className="mt-12 flex justify-center">
          <Link
            data-testid="ofrecemos-btn-comparar"
            to="/drones/comparar"
            onClick={() => track("comparar_agras_click", { origen: "portada" })}
            className="inline-flex h-14 items-center gap-3 rounded-full border border-certero px-9 font-display text-sm font-bold uppercase tracking-[0.15em] text-certero transition-[background-color,color] duration-300 hover:bg-certero hover:text-ink"
          >
            Comparar los Agras <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
